import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, of, tap } from 'rxjs';
import { BackendService, HttpOptions } from '@ipg/ngx-frontend-toolset';

export interface InvoiceSetupStatusResponse {
  isComplete: boolean;
  onboardingCompleted: boolean;
}

export interface InvoiceSetupRequest {
  purchaseNumber: string;
  invoiceEmail: string;
}

export interface InvoiceSetupSubmitResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class InvoiceSetupService {
  private readonly _backend = inject(BackendService);

  /**
   * Session cache of the onboarding flag.
   *
   * `'unknown'` is kept distinct from `'pending'` on purpose: a failed status
   * request must not be read as "onboarding pending", or a status outage would
   * strip the navigation for every user (the guard deliberately fails open).
   *
   * Only the `'completed'` state is cached across navigations — onboarding can
   * be finished mid-session, but it can never become incomplete again, so a
   * `'completed'` is safe to trust for the rest of the session. That cache is
   * what keeps the guard cheap now that it runs on every navigation.
   */
  private readonly _status = signal<'unknown' | 'pending' | 'completed'>(
    'unknown'
  );

  /** True only when the backend affirmatively said onboarding is not done. */
  readonly onboardingPending = computed(() => this._status() === 'pending');

  getStatus(options?: HttpOptions): Observable<InvoiceSetupStatusResponse> {
    return this._backend.get<InvoiceSetupStatusResponse>(
      '/invoice-setup/status',
      options
    );
  }

  /**
   * Resolve the onboarding flag, hitting the backend only while it is still
   * pending. Consumed by `onboardingGuard` on every navigation.
   */
  resolveOnboardingCompleted(): Observable<boolean> {
    if (this._status() === 'completed') {
      return of(true);
    }

    return this.getStatus().pipe(
      map((status: InvoiceSetupStatusResponse) => status.onboardingCompleted),
      tap((completed: boolean) =>
        this._status.set(completed ? 'completed' : 'pending')
      )
    );
  }

  submit(
    data: InvoiceSetupRequest,
    options?: HttpOptions
  ): Observable<InvoiceSetupSubmitResponse> {
    return this._backend
      .post<InvoiceSetupSubmitResponse>('/invoice-setup', data, options)
      .pipe(tap(() => this._status.set('completed')));
  }
}
